import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getCurrentIdToken, logout as cognitoLogout } from './auth';
import { decodeIdToken, type DecodedClaims } from './jwt';

interface AuthState {
  loading: boolean;
  idToken?: string;
  claims?: DecodedClaims;
  isAdmin: boolean;
  /**
   * 2026-10-21: ein reiner Kiosk-Zugang -- sieht nur die Kiosk-Einrichtung. Optional, damit die
   * vielen bestehenden Test-Attrappen von `useAuth` ohne das Feld als „kein Kiosk" gelten.
   */
  isKiosk?: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string>();
  const [claims, setClaims] = useState<DecodedClaims>();

  const refresh = useCallback(async () => {
    const token = await getCurrentIdToken();
    setIdToken(token);
    setClaims(token ? decodeIdToken(token) : undefined);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // v0.10 F1 (code review): a tab opened *before* a kiosk binding used to keep
  // the whole admin shell -- nav, „Historische Buchungen löschen", and the
  // member names it had already rendered -- because this provider reads storage
  // once on mount and the binding only cleared the binding tab's in-memory
  // token. Its data calls 401'd, so nothing new was readable and nothing
  // writable, but a stale shell full of real names is not "the login page",
  // which is what FR-183 promises however the admin area is reached.
  //
  // The `storage` event is delivered to every *other* same-origin document, so
  // it is exactly the signal that another tab signed this device out. Not
  // filtered by key on purpose: reacting to every storage change costs one
  // cheap re-read (getSession() is served from storage when the session is
  // valid) and cannot go stale if the session's key names ever change --
  // the wrong way round to be clever about for a containment property.
  useEffect(() => {
    const onStorage = () => void refresh();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // Stable identity on purpose: KioskRoute (App.tsx) depends on it in an effect
  // that must run once per kiosk entry, not on every render of the provider.
  const logout = useCallback(() => {
    cognitoLogout();
    setIdToken(undefined);
    setClaims(undefined);
  }, []);

  const isAdmin = claims?.groups.includes('admins') ?? false;
  // 2026-10-21: dieselbe Regel wie am Server (`rolleAusGruppen`) -- Kiosk nur, wenn das Konto
  // sonst keine Rolle trägt. Nur für die Oberfläche; was es darf, entscheidet der Server.
  const isKiosk =
    (claims?.groups.includes('kiosk') ?? false) &&
    !isAdmin &&
    !(claims?.groups.includes('chairs') ?? false);

  return (
    <AuthContext.Provider value={{ loading, idToken, claims, isAdmin, isKiosk, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
